/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import _ from "underscore"; // Npm: underscore.js library.
import { useQuery } from "@apollo/client"; // Npm: Apollo client.
import { HiArrowUturnDown, HiArrowPath } from "react-icons/hi2"; // Npm: react-icons library.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI Data Grid.
import { Chip } from "@mui/material";
import { Box, Flex, Text, Button, useBreakpointValue } from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DateRangePicker from "components/DateRangePicker";
import DownloadToExcel from "components/DownloadToExcel";
import TableSpinner from "components/TableSpinner";
import VendorReportDropDownOptions from "components/VendorReportDropDownOptions";

/*
 * GRAPHS
 */
import VendorReadQuery from "./__query__/index.vendor.read.query";

/*
 * OBJECTS
 */
const Index = () => {
  // Hook assignment.
  const [dateRange, setDateRange] = React.useState({
    selection: {
      startDate: Object.DateRangeProvider()?.startDate,
      endDate: Object.DateRangeProvider()?.endDate,
      key: "selection",
    },
  });
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );
  React.useEffect(
    () => () => _setFilterDebounced.cancel(),
    [_setFilterDebounced],
  );

  const _isFilterApplied = React.useMemo(
    () => filterModel?.items?.some((i) => i.value != void 0 && i.value !== ""),
    [filterModel],
  );
  const _appliedFilter = React.useMemo(
    () => _isFilterApplied ? filterModel : null,
    [_isFilterApplied, filterModel],
  );
  const _vendorReadVariables = React.useMemo(() => ({
    filter: _appliedFilter,
    skip: paginationModel?.pageSize * paginationModel?.page,
    take: paginationModel?.pageSize,
    smsStartDate: dateRange?.selection.startDate,
    smsEndDate: dateRange?.selection.endDate,
  }), [_appliedFilter, dateRange?.selection.endDate, dateRange?.selection.startDate, paginationModel?.page, paginationModel?.pageSize]);
  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);

  // Previous pagination uses Apollo cache-first so visited pages do not hit backend again.
  const _QueryVendorRead = useQuery(VendorReadQuery, {
    variables: _vendorReadVariables,
    fetchPolicy: _isPreviousPageRef.current ?
      "cache-first" :
      "cache-and-network",
  });

  const _rowCountRef = React.useRef(0);
  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile =
    useBreakpointValue({ base: true, md: false }, { fallback: "md" }) ?? false;
      
  const _HandleRefresh = React.useCallback(() => {
    _QueryVendorRead.refetch()
  }, [_QueryVendorRead.refetch])
  const _HandlePaginationModelChange = React.useCallback((__paginationModel) => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize;

    _paginationModelRef.current = __paginationModel;
    setPaginationModel(__paginationModel);
  }, []);
  const _HandleColumnVisibilityModelChange = React.useCallback((newModel) =>
    setColumnVisibilityModel(newModel), []);

  // Data assignment.
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 130,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        editable: false,
        sortable: false,
        filterable: false,
        headerAlign: "center",
        hide: true,
      },
      {
        field: "displayName",
        headerName: "Vendor Name",
        description: "Name of the vendor.",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 250,
      },
      {
        field: "totalCount",
        headerName: "Total Count",
        description: "Total SMS count.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "smsDelivered",
        headerName: "SMS Delivered",
        description: "Total delivered SMS.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "smsPending",
        headerName: "SMS Pending",
        description: "Total pending SMS.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "smsFailed",
        headerName: "SMS Failed",
        description: "Total failed SMS.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "smsUndeliverable",
        headerName: "SMS Undeliverable",
        description: "Total undeliverable SMS.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "smsRejected",
        headerName: "SMS Rejected",
        description: "Total rejected SMS.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "dlrPercent",
        headerName: "DLR %",
        description: "Delivery Rate Percentage.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,

        renderCell: ({ value }) => {
          const numeric = parseFloat(value);
          const bg =
            numeric >= 85 ? "#22c55e" : numeric >= 50 ? "#f59e0b" : "#ef4444";

          return (
            <Chip
              label={value ?? "-"}
              size="small"
              sx={{
                backgroundColor: `${bg} !important`,
                color: "white !important",
                fontWeight: 500,
                fontSize: "11px",
              }}
            />
          );
        },
      },
      {
        field: "action",
        headerName: "Actions",
        description: "Actions for vendor.",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <VendorReportDropDownOptions
            vendorId={row?.id}
            currency={row?.currency}
          />
        ),
      },
    ],
    [],
  );

  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryVendorRead?.data?.VendorRead?.map((r, index) =>
          "READ_SUCCESSFUL" === r.status
            ? {
                id:
                  r?.id ??
                  `vendor-report-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
                sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                displayName: r?.displayName ?? "-",
                totalCount:
                  r?._smsCountForGivenVendor?.[r?.id]?.smsCount ?? "0",
                smsDelivered:
                  r?._smsCountForGivenVendor?.[r?.id]?.smsDelivered ?? "0",
                smsPending:
                  r?._smsCountForGivenVendor?.[r?.id]?.smsAccepted ?? "0",
                smsFailed:
                  r?._smsCountForGivenVendor?.[r?.id]?.smsFailed ?? "0",
                smsUndeliverable:
                  r?._smsCountForGivenVendor?.[r?.id]?.smsUndelivered ?? "0",
                smsRejected:
                  r?._smsCountForGivenVendor?.[r?.id]?.smsRejected ?? "0",
                dlrPercent: (() => {
                  const totalCount =
                    r?._smsCountForGivenVendor?.[r?.id]?.smsCount;
                  const smsDelivered =
                    r?._smsCountForGivenVendor?.[r?.id]?.smsDelivered;
                  if (totalCount && smsDelivered && totalCount > 0) {
                    return ((smsDelivered / totalCount) * 100).toFixed(2) + "%";
                  }
                  return "0";
                })(),
                currency: r?.Billing?.currency,
              }
            : null,
        ),
      ),
    [
      _QueryVendorRead?.data?.VendorRead,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );
  const _rowCount = _.first(_QueryVendorRead?.data?.VendorRead)?._totalCount;
  const rowCount = React.useMemo(() => {
    if (undefined !== _rowCount && null !== _rowCount)
      _rowCountRef.current = _rowCount;

    return _rowCountRef.current;
  }, [_rowCount]);
  const cellsData = React.useMemo(
    () =>
      _QueryVendorRead?.data?.VendorRead?.map((item, __index) =>
        "READ_SUCCESSFUL" === item.status
          ? {
              "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
              "Vendor Name": item?.displayName ?? "-",
              "Total Count":
                item?._smsCountForGivenVendor?.[item?.id]?.smsCount ?? "0",
              "Sms Delivered":
                item?._smsCountForGivenVendor?.[item?.id]?.smsDelivered ?? "0",
              "Sms Failed":
                item?._smsCountForGivenVendor?.[item?.id]?.smsFailed ?? "0",
              "Sms Pending":
                item?._smsCountForGivenVendor?.[item?.id]?.smsAccepted ?? "0",
              "Sms Undeliverable":
                item?._smsCountForGivenVendor?.[item?.id]?.smsUndelivered ??
                "0",
              "Sms Rejected":
                item?._smsCountForGivenVendor?.[item?.id]?.smsRejected ?? "0",
              "DLR %": (() => {
                const totalCount =
                  item?._smsCountForGivenVendor?.[item?.id]?.smsCount;
                const smsDelivered =
                  item?._smsCountForGivenVendor?.[item?.id]?.smsDelivered;
                if (totalCount && smsDelivered && totalCount > 0) {
                  return ((smsDelivered / totalCount) * 100).toFixed(2) + "%";
                }
                return "0";
              })(),
            }
          : void 0,
      ),
    [
      _QueryVendorRead?.data?.VendorRead,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );
  const headersData = React.useMemo(() => [
    "S.No.",
    "Vendor Name",
    "Total Count",
    "Sms Delivered",
    "Sms Failed",
    "Sms Pending",
    "Sms Undeliverable",
    "Sms Rejected",
    "DLR %",
  ].map((i) => ({ key: i, label: i })), [])
  const slots = React.useMemo(() => ({
    loadingOverlay: () => (
      <TableSpinner
        isLoading={true}
        isEmpty={false}
        chopHeightFromHundredPercentage={
          _tableHeaderHeightRef?.current?.clientHeight
        }
      />
    ),
    noResultsOverlay: () => (
      <TableSpinner
        isLoading={false}
        isEmpty={true}
        chopHeightFromHundredPercentage={
          _tableHeaderHeightRef?.current?.clientHeight
        }
      />
    ),
    noRowsOverlay: () => (
      <TableSpinner
        isLoading={false}
        isEmpty={true}
        chopHeightFromHundredPercentage={
          _tableHeaderHeightRef?.current?.clientHeight
        }
      />
    ),
  }), [])

  // Return component.
  return (
    <>
      <Flex
        className="reportsVendor base"
        height="auto"
        minH="max-content"
        overflow="visible"
      >
        <Flex
          display="flex"
          flex="0 0 auto"
          flexDirection="column"
          position="relative"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          minH="calc(100vh - 20px)"
          height="auto"
          overflow="visible"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex pb="0px" justify="space-between" alignItems="center" gap="22px">
            <Flex flex={0.5} gap="12px" flexDir="column">
              <Text
                as="h3"
                fontSize={17}
                fontWeight={500}
                display="flex"
                alignItems="center"
                color="brand.500"
                gap="6px"
              >
                <HiArrowUturnDown />
                {_isCurrentViewMobile
                  ? "Filter Rows"
                  : "Filter Rows based on Date."}
              </Text>
              <DateRangePicker
                startDate={dateRange?.selection?.startDate}
                endDate={dateRange?.selection?.endDate}
                w="100%"
                fontSize="13px"
                height="max-content"
                gap="6px"
                setSelectDate={setDateRange}
              />
            </Flex>
            <Flex
              flex={0.5}
              justifyContent="flex-end"
              gap="12px"
              alignItems="center"
            >
              <Button
                borderRadius="12px"
                fontWeight="450"
                maxW="250px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                onClick={_HandleRefresh}
                isDisabled={_QueryVendorRead?.loading}
                leftIcon={<HiArrowPath />}
              >
                Refresh
              </Button>
              <DownloadToExcel
                cellsData={cellsData}
                headersData={headersData}
              />
            </Flex>
          </Flex>
          <Box
            w="100%"
            height="calc(100vh - 150px)"
            minH="520px"
            flexShrink={0}
            overflow="hidden"
          >
            <DataGrid
              rows={rows}
              columns={columns}
              rowHeight={48}
              rowCount={rowCount}
              paginationMode="server"
              filterMode="server"
              disableColumnSorting={true}
              filterModel={filterModel}
              onFilterModelChange={_setFilterDebounced}
              paginationModel={paginationModel}
              onPaginationModelChange={_HandlePaginationModelChange}
              pageSizeOptions={[10, 30, 50, 100]}
              slots={slots}
              disableRowSelectionOnClick
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={_HandleColumnVisibilityModelChange}
              loading={_QueryVendorRead?.loading}
            />
          </Box>
        </Flex>
      </Flex>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {};

/*
 * EXPORTS
 */
export default React.memo(Index);
